import { isPremiumTier } from '@/lib/tier';
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface HdAudioPreferenceValue {
  /** Whether HD Audio is currently enabled by the user */
  isHdEnabled: boolean;
  /** Toggle HD Audio preference (persists to localStorage) */
  setHdEnabled: (enabled: boolean) => void;
  /** Whether the HD Audio toggle should be visible (premium + feature flag) */
  shouldShowToggle: boolean;
  /** Whether the feature flag is still loading */
  isFeatureFlagLoading: boolean;
}

const HdAudioPreferenceContext = createContext<HdAudioPreferenceValue | null>(null);

export function HdAudioPreferenceProvider({ children }: { children: ReactNode }) {
  const { user, tier, tierInitialized } = useAuth();
  const [isHdEnabled, setHdEnabledState] = useState(false);

  // Feature flag: query app_config with 5-minute staleTime
  const { data: featureEnabled = false, isLoading: isFeatureFlagLoading } = useQuery({
    queryKey: ['app-config', 'hd_audio_enabled'],
    queryFn: async () => {
      const { data } = await supabase
        .from('app_config')
        .select('value')
        .eq('key', 'hd_audio_enabled')
        .maybeSingle();
      return data?.value === 'true';
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!user,
  });

  const isPremiumOrAdmin = isPremiumTier(tier);
  const shouldShowToggle = featureEnabled && isPremiumOrAdmin;

  // Load preference from localStorage once user is known and the feature flag
  // has resolved. Reading before the flag resolves is fine, but we re-read
  // after resolution to recover from any transient false state.
  useEffect(() => {
    if (!user?.id) return;
    if (isFeatureFlagLoading) return;
    const stored = localStorage.getItem(`leka-hd-audio-${user.id}`);
    setHdEnabledState(stored === 'true');
  }, [user?.id, isFeatureFlagLoading]);

  // Cleanup on downgrade: only after BOTH tier and feature flag have resolved.
  // Otherwise the in-flight feature flag (defaulting to false) would briefly
  // make `shouldShowToggle` false for an eligible premium user and wipe their
  // saved preference on every app load.
  useEffect(() => {
    if (!tierInitialized || isFeatureFlagLoading) return;
    if (!user?.id) return;
    if (shouldShowToggle) return;
    localStorage.removeItem(`leka-hd-audio-${user.id}`);
    setHdEnabledState(false);
  }, [tierInitialized, isFeatureFlagLoading, shouldShowToggle, user?.id]);

  const setHdEnabled = useCallback((enabled: boolean) => {
    if (!user?.id) return;
    localStorage.setItem(`leka-hd-audio-${user.id}`, String(enabled));
    setHdEnabledState(enabled);
  }, [user?.id]);

  return (
    <HdAudioPreferenceContext.Provider value={{ isHdEnabled, setHdEnabled, shouldShowToggle, isFeatureFlagLoading }}>
      {children}
    </HdAudioPreferenceContext.Provider>
  );
}

export function useHdAudioPreference(): HdAudioPreferenceValue {
  const ctx = useContext(HdAudioPreferenceContext);
  if (!ctx) throw new Error('useHdAudioPreference must be used within HdAudioPreferenceProvider');
  return ctx;
}
